import { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

/**
 * Hook to fetch all events for the current admin
 * Returns real-time updates of events
 */
export const useEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Create query for all events (accessible to all admins)
    const eventsQuery = query(
      collection(db, 'events'),
      orderBy('eventDate', 'desc')
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEvents(eventsData);
        setLoading(false);
      },
      (err) => {
        logger.error('Error fetching events:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [currentUser]);

  return { events, loading, error };
};

/**
 * Hook to fetch a single event by ID
 * @param {string} eventId - The event ID
 * @param {boolean} realtime - Whether to use real-time listener (default: false)
 * Returns real-time updates of the event OR one-time fetch
 */
export const useEvent = (eventId, realtime = false) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const eventRef = doc(db, 'events', eventId);

    if (realtime) {
      // Real-time listener (for admin ONLY)
      let unsubscribed = false;

      const unsubscribe = onSnapshot(
        eventRef,
        (snapshot) => {
          if (unsubscribed) return;
          if (snapshot.exists()) {
            setEvent({ id: snapshot.id, ...snapshot.data() });
          } else {
            setEvent(null);
            setError('Event not found');
          }
          setLoading(false);
        },
        (err) => {
          if (unsubscribed) return;
          logger.error('Error fetching event:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => {
        unsubscribed = true;
        unsubscribe();
      };
    } else {
      // One-time fetch (for gallery - saves read costs!)
      getDoc(eventRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            setEvent({ id: snapshot.id, ...snapshot.data() });
          } else {
            setEvent(null);
            setError('Event not found');
          }
          setLoading(false);
        })
        .catch((err) => {
          logger.error('Error fetching event:', err);
          setError(err.message);
          setLoading(false);
        });

      return undefined;
    }
  }, [eventId, realtime]);

  return { event, loading, error };
};

/**
 * Create a new event
 */
export const createEvent = async (eventData, userId, userEmail = null) => {
  try {
    const eventRef = await addDoc(collection(db, 'events'), {
      ...eventData,
      createdBy: userId,
      createdByEmail: eventData.createdByEmail || userEmail || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      videoCount: 0,
      status: 'active',
      isPublic: eventData.isPublic !== undefined ? eventData.isPublic : true // Default to public
    });

    return { success: true, id: eventRef.id };
  } catch (err) {
    logger.error('Error creating event:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Update an existing event
 */
export const updateEvent = async (eventId, eventData) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      ...eventData,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (err) {
    logger.error('Error updating event:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete an event
 */
export const deleteEvent = async (eventId) => {
  try {
    // Delete the event document
    // Note: This automatically deletes clientEmail and clientPassword fields
    // since they're stored in the event document
    const eventRef = doc(db, 'events', eventId);
    await deleteDoc(eventRef);

    // TODO: Consider deleting associated videos and storage files
    // This would require querying all videos for this event and deleting them
    // For now, videos remain orphaned but admin can clean up manually

    return { success: true };
  } catch (err) {
    logger.error('Error deleting event:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get a single event by ID (one-time fetch, not real-time)
 */
export const getEvent = async (eventId) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    const eventSnap = await getDoc(eventRef);

    if (eventSnap.exists()) {
      return {
        success: true,
        event: { id: eventSnap.id, ...eventSnap.data() }
      };
    } else {
      return { success: false, error: 'Event not found' };
    }
  } catch (err) {
    logger.error('Error getting event:', err);
    return { success: false, error: err.message };
  }
};
